import React from 'react';
import { makeStyles } from '@material-ui/core';
import i18n from 'i18n-js';
import CheckIcon from '@material-ui/icons/Check';

import { theme } from 'config/theme';

interface WizardStepperProps {
    currentStep: number;
    onStepClick?: (stepIndexZeroBased: number) => void;
}

const STEP_LABELS: string[] = [
    'tableMaps.wizard.steps.basicInformation',
    'tableMaps.wizard.steps.mapConfiguration',
    'tableMaps.wizard.steps.roomFeatures',
    'tableMaps.wizard.steps.tablePlacement',
    'tableMaps.wizard.steps.tableDetails',
    'tableMaps.wizard.steps.review',
];

const TOTAL_STEPS = STEP_LABELS.length;

const useStyles = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '24px 0 0',
        background: 'transparent',
    },
    stepWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
    },
    stepWrapperClickable: {
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.75,
        },
    },
    stepRow: {
        display: 'flex',
        alignItems: 'center',
    },
    circle: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 600,
        flexShrink: 0,
        position: 'relative' as const,
        zIndex: 1,
    },
    circleActive: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        border: `2px solid ${theme.palette.primary.main}`,
    },
    circleActiveWithRing: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        border: `2px solid ${theme.palette.primary.main}`,
        boxShadow: `0 0 0 2px white, 0 0 0 4px ${theme.palette.primary.main}`,
    },
    circleCompleted: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        border: `2px solid ${theme.palette.primary.main}`,
    },
    circleUpcoming: {
        backgroundColor: 'transparent',
        color: theme.palette.secondary.main,
        border: `2px solid ${theme.palette.secondary.main}`,
    },
    circleClickableUpcoming: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        border: `2px solid ${theme.palette.primary.main}`,
    },
    connector: {
        height: 2,
        flex: 1,
        minWidth: 24,
        maxWidth: 80,
        marginTop: 15,
        alignSelf: 'flex-start',
        backgroundColor: theme.palette.secondary.light,
        marginLeft: 0,
        marginRight: 0,
    },
    connectorCompleted: {
        backgroundColor: theme.palette.primary.main,
    },
    label: {
        marginTop: 6,
        fontSize: 11,
        textAlign: 'center',
        maxWidth: 80,
        lineHeight: 1.3,
    },
    labelActive: {
        color: theme.palette.primary.main,
        fontWeight: 600,
    },
    labelCompleted: {
        color: theme.palette.primary.main,
        fontWeight: 500,
    },
    labelUpcoming: {
        color: theme.palette.secondary.main,
        fontWeight: 400,
    },
    labelClickableUpcoming: {
        color: theme.palette.primary.main,
        fontWeight: 500,
    },
    checkIcon: {
        fontSize: 16,
    },
});

export default function WizardStepper({ currentStep, onStepClick }: WizardStepperProps) {
    const classes = useStyles();

    return (
        <div className={classes.root}>
            {STEP_LABELS.map((labelKey, index) => {
                const stepNumber = index + 1;
                const isCompleted = stepNumber < currentStep;
                const isActive = stepNumber === currentStep;
                const isClickable = onStepClick !== undefined;

                const isClickableUpcoming = !isCompleted && !isActive && isClickable;
                const circleClass = isCompleted
                    ? classes.circleCompleted
                    : isActive
                    ? onStepClick
                        ? classes.circleActiveWithRing
                        : classes.circleActive
                    : isClickableUpcoming
                    ? classes.circleClickableUpcoming
                    : classes.circleUpcoming;

                const labelClass = isCompleted
                    ? classes.labelCompleted
                    : isActive
                    ? classes.labelActive
                    : isClickableUpcoming
                    ? classes.labelClickableUpcoming
                    : classes.labelUpcoming;

                return (
                    <React.Fragment key={index}>
                        <div
                            className={`${classes.stepWrapper} ${
                                isClickable ? classes.stepWrapperClickable : ''
                            }`}
                            onClick={isClickable ? () => onStepClick(index) : undefined}
                        >
                            <div className={classes.stepRow}>
                                <div className={`${classes.circle} ${circleClass}`}>
                                    {isCompleted ? (
                                        <CheckIcon className={classes.checkIcon} />
                                    ) : (
                                        stepNumber
                                    )}
                                </div>
                            </div>
                            <span className={`${classes.label} ${labelClass}`}>
                                {i18n.t(labelKey)}
                            </span>
                        </div>
                        {index < TOTAL_STEPS - 1 && (
                            <div
                                className={`${classes.connector} ${
                                    isCompleted ? classes.connectorCompleted : ''
                                }`}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}
